import { NativeAppControlRule } from "@/api/classroomAppControls";
import { AppliedClassroomRule, AppliedClassroomRulesList, CreateInClassRuleRequest, PatchInClassRuleRequest } from "@/api/classroomRules";
import { AppliedList, List, RuleAction } from "../types";
import { compareByCreatedAtDesc, toRuleAction, toRuleType } from "../utils";
import { ApplyScope, InClassRule, InClassRuleTargetType, NewClassroomRule } from "./types";

export function toInClassRule(applied: AppliedClassroomRule): InClassRule {
	return {
		id: applied.rule.id,
		target: applied.rule.target,
		type: applied.rule.type,
		action: toRuleAction(applied.rule.action),
		applyTo: applied.wholeClass ? "class" : "students",
		enabled: applied.enabled,
		studentIds: applied.studentIds,
		effective: applied.effective,
		conflictedBy: applied.conflictedBy,
		overriddenBy: applied.overriddenBy,
	};
}

export function toAppliedList(list: AppliedClassroomRulesList): AppliedList {
	return {
		id: list.id,
		title: list.name,
		teacherId: list.teacherId,
		teacherName: list.teacherName,
		teacherEmail: list.teacherEmail,
		teacherUsername: list.teacherUsername,
		rules: [...list.classroomRules]
			.sort((a, b) => compareByCreatedAtDesc(a.rule, b.rule))
			.map((applied) => ({
				id: applied.rule.id,
				target: applied.rule.target,
				action: toRuleAction(applied.rule.action),
				type: applied.rule.type,
				enabled: applied.enabled,
				effective: applied.effective,
				conflictedBy: applied.conflictedBy,
				overriddenBy: applied.overriddenBy,
			})),
	};
}

/**
 * Synthetic ID for an app-control rule. Each stored row is its own rule: whole class or one student.
 */
export function appControlRuleId(row: Pick<NativeAppControlRule, "id">): string {
	return `appControl:${row.id}`;
}

/**
 * Maps each app-control row to an in-class rule. Storage is one row per audience
 * (whole class or a single student), matching the legacy policy drawer.
 */
export function groupAppControlRules(rows: NativeAppControlRule[]): InClassRule[] {
	return rows.map((row) => ({
		id: appControlRuleId(row),
		target: row.appControlId,
		type: "APP_CONTROL",
		action: row.action,
		applyTo: row.isClassroomRule ? "class" : "students",
		studentIds: row.studentStableIdV1 ? [row.studentStableIdV1] : [],
		// App-control storage has no enabled flag and no conflict model, so these rules are
		// always on and always effective.
		enabled: true,
		effective: true,
		appControlRows: [row],
	}));
}

/**
 * Combines classroom rules and app-controls for lookup. Active Rules renders them as
 * separate sections; classroom rules stay first, newest first, because app-control rows carry no
 * created timestamp to interleave them by.
 */
export function mergeInClassRules(classroomRules: AppliedClassroomRule[], appControlRows: NativeAppControlRule[]): InClassRule[] {
	const rules = [...classroomRules].sort((a, b) => compareByCreatedAtDesc(a.rule, b.rule)).map(toInClassRule);
	return [...rules, ...groupAppControlRules(appControlRows)];
}

/** Same total as Active Rules: in-class rows plus every rule on applied lists. */
export function countActiveRules(inClassRules: InClassRule[], lists: List[]): number {
	return inClassRules.length + lists.reduce((sum, list) => sum + list.rules.length, 0);
}

export function isAppControlRule(rule: InClassRule): rule is InClassRule & { type: "APP_CONTROL" } {
	return rule.type === "APP_CONTROL";
}

type StudentIdFields = {
	id: string;
	stableIdV1?: string;
	qoriaUserId?: string;
};

/**
 * Classroom-rules persist Qoria IDs; app-controls persist stable IDs. Students missing the
 * ID the destination API needs are dropped rather than sending the wrong kind.
 */
export function persistableStudentIds(
	students: StudentIdFields[],
	selectedIds: string[],
	type: InClassRuleTargetType | undefined
): string[] {
	const useStableId = type === "APP_CONTROL";
	return students
		.filter((student) => selectedIds.includes(student.id))
		.map((student) => (useStableId ? student.stableIdV1 : student.qoriaUserId))
		.filter((id): id is string => Boolean(id));
}

/** Maps persisted student IDs back to the checkbox row keys used in the class-rule form. */
export function selectedStudentRowIds(
	students: StudentIdFields[],
	ruleStudentIds: string[],
	type: InClassRuleTargetType | undefined
): string[] {
	const useStableId = type === "APP_CONTROL";
	return students
		.filter((student) => {
			const persistedId = useStableId ? student.stableIdV1 : student.qoriaUserId;
			return persistedId !== undefined && ruleStudentIds.includes(persistedId);
		})
		.map((student) => student.id);
}

type AudienceStudent = StudentIdFields & { name: string };

export type InClassAudience = { kind: "class" } | { kind: "one"; name: string } | { kind: "many"; count: number };

export interface ListRuleOverride {
	listTitle: string;
	/** Action of the effective list rule the class rule would replace. */
	listAction: RuleAction;
}

/**
 * Indexes effective applied-list rules by target so a class-rule draft can be checked
 * without scanning the lists again, and without another request. The classroom-rules
 * response already holds every applied list, and a class is capped at 200 rules.
 * A whole-class rule overrides a list rule only when the target matches and the action differs.
 */
export function indexEffectiveListRules(lists: readonly AppliedList[]): Map<string, ListRuleOverride[]> {
	const byTarget = new Map<string, ListRuleOverride[]>();

	for (const list of lists) {
		for (const rule of list.rules) {
			if (rule.effective === false || rule.target.trim() === "") {
				continue;
			}
			const key = rule.target.trim().toLowerCase();
			const entry: ListRuleOverride = { listTitle: list.title, listAction: rule.action };
			const existing = byTarget.get(key);
			if (existing) {
				existing.push(entry);
			} else {
				byTarget.set(key, [entry]);
			}
		}
	}

	return byTarget;
}

/**
 * The first effective list rule a whole-class draft would override.
 * Only one list rule for a target stays effective, so the notice names that list.
 * Same action is not an override.
 */
export function listRuleOverriddenByClassRule(
	index: Map<string, ListRuleOverride[]>,
	candidate: { target: string; action: RuleAction }
): ListRuleOverride | undefined {
	const key = candidate.target.trim().toLowerCase();
	if (key === "") {
		return undefined;
	}

	return (index.get(key) ?? []).find((rule) => rule.listAction !== candidate.action);
}

/** Whole class, a single student's name, or a count when the rule is limited to some students. */
export function inClassAudience(rule: Pick<InClassRule, "applyTo" | "studentIds" | "type">, students: AudienceStudent[]): InClassAudience {
	if (rule.applyTo === "class") {
		return { kind: "class" };
	}

	if (rule.studentIds.length === 1) {
		const [rowId] = selectedStudentRowIds(students, rule.studentIds, rule.type);
		const student = students.find((candidate) => candidate.id === rowId);
		if (student?.name) {
			return { kind: "one", name: student.name };
		}
	}

	return { kind: "many", count: rule.studentIds.length };
}

/** Every app-control row ID in the given rules, for the batch delete the app-controls API takes. */
export function appControlRowIds(rules: InClassRule[]): string[] {
	return rules.flatMap((rule) => (rule.appControlRows ?? []).map((row) => row.id));
}

/**
 * The rule ID is generated in the browser rather than by the API so that a validation error, which
 * names the rule it rejected, can be matched back to the rule the teacher was creating.
 */
export function buildCreateInClassRuleRequest(rule: NewClassroomRule): CreateInClassRuleRequest {
	const wholeClass = rule.applyTo === "class";

	return {
		rule: {
			id: rule.id,
			target: rule.target,
			type: rule.type,
			action: toRuleType(rule.action),
		},
		wholeClass,
		enabled: true,
		studentIds: wholeClass ? [] : rule.studentIds,
	};
}

/**
 * Builds a PATCH body carrying only what changed. Omitted fields are left untouched by the API,
 * and sending `wholeClass` without `studentIds` (or the reverse) fails its consistency check.
 */
export function buildPatchInClassRuleRequest(
	original: InClassRule,
	change: { action?: RuleAction; enabled?: boolean; applyTo?: ApplyScope; studentIds?: string[] }
): PatchInClassRuleRequest {
	const body: PatchInClassRuleRequest = {};

	if (change.action !== undefined && change.action !== original.action) {
		body.action = toRuleType(change.action);
	}

	if (change.enabled !== undefined && change.enabled !== original.enabled) {
		body.enabled = change.enabled;
	}

	if (change.applyTo !== undefined) {
		const wholeClass = change.applyTo === "class";
		body.wholeClass = wholeClass;
		body.studentIds = wholeClass ? [] : (change.studentIds ?? original.studentIds ?? []);
	} else if (change.studentIds !== undefined) {
		body.wholeClass = false;
		body.studentIds = change.studentIds;
	}

	return body;
}
